<div class="firmware-panel">
    <!-- Loading State -->
    <div *ngIf="loading" class="panel-content loading-state">
        <div class="loader"></div>
        <p>{{ 'GENERAL.LOADING' | translate }}</p>
    </div>

    <!-- Readonly Mode -->
    <div *ngIf="!isEditMode && firmware && !loading" class="readonly-view">
        <div class="panel-content">
            <!-- Name Box -->
            <div class="box">
                <div class="box-title">
                    <h2>{{ firmware.name }}</h2>
                    <span class="version-badge">v{{ firmware.version }}</span>
                </div>
            </div>

            <!-- Details Box: Description, Release Notes, Date, Regions -->
            <div class="box">
                <!-- Description -->
                <div class="box-section" *ngIf="firmware.description">
                    <h3>{{ 'FEATURES.FIRMWARE.SECTIONS.DESCRIPTION' | translate }}</h3>
                    <p class="description-text long-text">{{ firmware.description }}</p>
                </div>

                <!-- Release Notes -->
                <div class="box-section" *ngIf="firmware.releaseNotes">
                    <h3>{{ 'FEATURES.FIRMWARE.SECTIONS.RELEASE_NOTES' | translate }}</h3>
                    <p class="release-notes-text long-text">{{ firmware.releaseNotes }}</p>
                </div>

                <!-- Release Date -->
                <div class="box-section">
                    <h3>{{ 'FEATURES.FIRMWARE.SECTIONS.RELEASED' | translate }}</h3>
                    <p>{{ firmware.releaseDate | date: 'yyyy-MM-dd' }}</p>
                </div>

                <!-- Regions -->
                <div class="box-section" *ngIf="firmware.regions && firmware.regions.length > 0">
                    <h3>{{ 'FEATURES.FIRMWARE.SECTIONS.REGIONS' | translate }}</h3>
                    <div class="regions-list">
                        <span class="region-badge" *ngFor="let region of firmware.regions">
                            {{ region.name }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="box">
                <div class="box-section">
                    <h3>{{ 'FEATURES.FIRMWARE.SECTIONS.USERS' | translate }}</h3>
                    <p *ngIf="firmware.userIds.length === 0">
                        {{ 'FEATURES.FIRMWARE.TEXTS.ALL_USERS' | translate }}
                    </p>
                    <ul *ngIf="firmware.userIds.length > 0">
                        <li *ngFor="let name of selectedUserNames">{{ name }}</li>
                    </ul>
                </div>
            </div>

            <!-- Files -->
            <div class="detail-section" *ngIf="firmware.files && firmware.files.length > 0">
                <h3>{{ 'FEATURES.FIRMWARE.SECTIONS.FILES' | translate }}</h3>
                <app-table [loading]="false">
                    <tr thead>
                        <app-table-header [width]="8" [text]="'FEATURES.FIRMWARE.COLUMNS.UPLOAD_ORDER' | translate" />
                        <app-table-header
                            [dynamicWidth]="100"
                            [text]="'FEATURES.FIRMWARE.COLUMNS.FILE_NAME' | translate"
                        />
                        <app-table-header [width]="14" [text]="'FEATURES.FIRMWARE.COLUMNS.FILE_SIZE' | translate" />
                    </tr>
                    <ng-container tbody>
                        <tr *ngFor="let file of getDisplayFiles(); let i = index">
                            <td class="text-center">{{ i + 1 }}</td>
                            <td>{{ file.name }}</td>
                            <td>{{ formatFileSize(file.size) }}</td>
                        </tr>
                    </ng-container>
                </app-table>
            </div>

            <div class="form-actions">
                <button type="button" (click)="enterEditMode()">
                    {{ 'BUTTONS.EDIT' | translate }}
                </button>
            </div>
        </div>
    </div>

    <!-- Edit Mode -->
    <div *ngIf="isEditMode" class="edit-view">
        <form [formGroup]="firmwareForm" (ngSubmit)="saveAndExitEdit()" autocomplete="off">
            <div class="panel-content" [class.loading]="loading">
                <app-form-section>
                    <app-form-row>
                        <app-form-group>
                            <label for="name">{{ 'FEATURES.FIRMWARE.COLUMNS.NAME' | translate }}</label>
                            <input
                                appInputValidator
                                [checkValidity]="showPotentialValidationMessages"
                                id="name"
                                formControlName="name"
                                type="text"
                                [placeholder]=""
                            />
                            <app-validation-message
                                *ngIf="showPotentialValidationMessages && isEditMode"
                                [control]="f.name"
                                languageKey="FEATURES.FIRMWARE.COLUMNS.NAME"
                            />
                        </app-form-group>
                    </app-form-row>

                    <app-form-row>
                        <app-form-group>
                            <label for="version">{{ 'FEATURES.FIRMWARE.COLUMNS.VERSION' | translate }}</label>
                            <input
                                appInputValidator
                                [checkValidity]="showPotentialValidationMessages"
                                id="version"
                                formControlName="version"
                                type="text"
                                [placeholder]=""
                            />
                            <app-validation-message
                                *ngIf="showPotentialValidationMessages && isEditMode"
                                [control]="f.version"
                                languageKey="FEATURES.FIRMWARE.COLUMNS.VERSION"
                            />
                        </app-form-group>
                    </app-form-row>

                    <app-form-row>
                        <app-form-group>
                            <label for="description">{{ 'FEATURES.FIRMWARE.COLUMNS.DESCRIPTION' | translate }}</label>
                            <textarea
                                appInputValidator
                                [checkValidity]="showPotentialValidationMessages"
                                id="description"
                                formControlName="description"
                                placeholder=""
                                rows="3"
                            ></textarea>
                            <app-validation-message
                                *ngIf="showPotentialValidationMessages && isEditMode"
                                [control]="f.description"
                                languageKey="FEATURES.FIRMWARE.COLUMNS.DESCRIPTION"
                            />
                        </app-form-group>
                    </app-form-row>

                    <app-form-row>
                        <app-form-group>
                            <label for="releaseNotes">{{
                                'FEATURES.FIRMWARE.SECTIONS.RELEASE_NOTES' | translate
                            }}</label>
                            <textarea
                                id="releaseNotes"
                                formControlName="releaseNotes"
                                placeholder=""
                                rows="3"
                            ></textarea>
                            <app-validation-message
                                *ngIf="showPotentialValidationMessages && isEditMode"
                                [control]="f.releaseNotes"
                                languageKey="FEATURES.FIRMWARE.SECTIONS.RELEASE_NOTES"
                            />
                        </app-form-group>
                    </app-form-row>

                    <app-form-row>
                        <app-form-group>
                            <label for="releaseDate">{{ 'FEATURES.FIRMWARE.SECTIONS.RELEASED' | translate }}</label>
                            <input
                                appInputValidator
                                [checkValidity]="showPotentialValidationMessages"
                                id="releaseDate"
                                formControlName="releaseDate"
                                type="date"
                            />
                            <app-validation-message
                                *ngIf="showPotentialValidationMessages && isEditMode"
                                [control]="f.releaseDate"
                                languageKey="FEATURES.FIRMWARE.SECTIONS.RELEASED"
                            />
                        </app-form-group>
                    </app-form-row>

                    <app-form-row>
                        <app-form-group>
                            <label for="regionIds">{{ 'FEATURES.FIRMWARE.SECTIONS.REGIONS' | translate }}</label>
                            <app-multi-select
                                id="regionIds"
                                [items]="cachedAllRegions"
                                formControlName="regionIds"
                                [searchable]="true"
                                [showToggleAll]="true"
                            />
                            <app-validation-message
                                *ngIf="showPotentialValidationMessages && isEditMode"
                                [control]="f.regionIds"
                                languageKey="FEATURES.FIRMWARE.SECTIONS.REGIONS"
                            />
                        </app-form-group>
                    </app-form-row>

                    <app-form-row>
                        <app-form-group>
                            <div class="form-label">{{ 'FEATURES.FIRMWARE.SECTIONS.USERS' | translate }}</div>
                            <app-checkbox
                                id="firmware-all-users"
                                formControlName="allUsers"
                                [text]="'FEATURES.FIRMWARE.TEXTS.ALL_USERS' | translate"
                            />
                            <p>{{ 'FEATURES.FIRMWARE.TEXTS.USER_VISIBILITY_HELP' | translate }}</p>
                        </app-form-group>
                    </app-form-row>

                    <app-form-row *ngIf="!f.allUsers.value">
                        <app-form-group>
                            <label for="firmware-userIds">{{ 'FEATURES.FIRMWARE.SECTIONS.USERS' | translate }}</label>
                            <app-multi-select
                                id="firmware-userIds"
                                [items]="cachedAllUsers"
                                formControlName="userIds"
                                [searchable]="true"
                                [loading]="usersLoading"
                                [showToggleAll]="false"
                                [showAllText]="false"
                            />
                            <p role="alert" *ngIf="usersLoadFailed">
                                {{ 'FEATURES.FIRMWARE.TEXTS.USERS_LOAD_ERROR' | translate }}
                            </p>
                            <app-validation-message
                                *ngIf="showPotentialValidationMessages"
                                [control]="f.userIds"
                                languageKey="FEATURES.FIRMWARE.SECTIONS.USERS"
                            />
                        </app-form-group>
                    </app-form-row>

                    <!-- File Upload Section -->
                    <app-form-row *ngIf="canManageFiles">
                        <app-form-group>
                            <div class="form-label">{{ 'FEATURES.FIRMWARE.SECTIONS.FILES' | translate }}</div>
                            <div
                                class="file-upload-area"
                                [class.drag-over]="dragOver"
                                (dragover)="onDragOver($event)"
                                (dragleave)="onDragLeave($event)"
                                (drop)="onDrop($event)"
                            >
                                <span class="material-symbols-rounded">cloud_upload</span>
                                <p>{{ 'FEATURES.FIRMWARE.TEXTS.DRAG_DROP_FILES' | translate }}</p>
                                <input
                                    type="file"
                                    #fileInput
                                    multiple
                                    hidden
                                    (change)="onFilesSelected(fileInput.files)"
                                />
                                <button type="button" (click)="fileInput.click()" class="file-select-btn">
                                    {{ 'BUTTONS.BROWSE' | translate }}
                                </button>
                            </div>
                        </app-form-group>
                    </app-form-row>

                    <!-- Files List -->
                    <app-form-row *ngIf="getAllFilesList().length > 0 || uploadedFilesList.length > 0">
                        <app-form-group>
                            <div class="form-label">{{ 'FEATURES.FIRMWARE.SECTIONS.FILES' | translate }}</div>
                            <app-table [loading]="false">
                                <tr thead>
                                    <app-table-header
                                        [width]="8"
                                        [text]="'FEATURES.FIRMWARE.COLUMNS.UPLOAD_ORDER' | translate"
                                    />
                                    <app-table-header
                                        [dynamicWidth]="100"
                                        [text]="'FEATURES.FIRMWARE.COLUMNS.FILE_NAME' | translate"
                                    />
                                    <app-table-header
                                        [width]="14"
                                        [text]="'FEATURES.FIRMWARE.COLUMNS.FILE_SIZE' | translate"
                                    />
                                    <app-table-header [width]="12" [text]="''" />
                                </tr>
                                <ng-container tbody>
                                    <tr
                                        *ngFor="let file of getAllFilesList(); let i = index"
                                        [class.removed]="file.isRemoved"
                                    >
                                        <td class="text-center">{{ i + 1 }}</td>
                                        <td>{{ file.name }}</td>
                                        <td>{{ formatFileSize(file.size) }}</td>
                                        <td class="file-actions">
                                            <button
                                                type="button"
                                                class="icon-button"
                                                (click)="moveFileUp(getAllFilesList(), i)"
                                                [disabled]="i === 0"
                                                title="Move up"
                                            >
                                                <span class="material-symbols-rounded">arrow_upward</span>
                                            </button>
                                            <button
                                                type="button"
                                                class="icon-button"
                                                (click)="moveFileDown(getAllFilesList(), i)"
                                                [disabled]="i === getAllFilesList().length - 1"
                                                title="Move down"
                                            >
                                                <span class="material-symbols-rounded">arrow_downward</span>
                                            </button>
                                            <button
                                                *ngIf="canManageFiles"
                                                type="button"
                                                class="icon-button"
                                                (click)="removeFileFromList(getAllFilesList(), i)"
                                                [title]="file.isRemoved ? 'Restore file' : 'Remove file'"
                                            >
                                                <span class="material-symbols-rounded">
                                                    {{ file.isRemoved ? 'restore' : 'delete' }}
                                                </span>
                                            </button>
                                        </td>
                                    </tr>
                                </ng-container>
                            </app-table>
                            <div
                                *ngIf="
                                    showPotentialValidationMessages &&
                                    !firmware &&
                                    canManageFiles &&
                                    getAllFilesList().length === 0
                                "
                                class="validation-error"
                            >
                                {{ 'FEATURES.FIRMWARE.SECTIONS.FILES' | translate }}
                                {{ 'GENERAL.IS_REQUIRED' | translate }}
                            </div>
                        </app-form-group>
                    </app-form-row>
                </app-form-section>

                <div class="form-actions">
                    <button type="button" (click)="cancelEdit()" [disabled]="loading">
                        {{ 'BUTTONS.CANCEL' | translate }}
                    </button>
                    <button type="submit" class="primary" [disabled]="loading">
                        {{ 'BUTTONS.SAVE' | translate }}
                    </button>
                </div>
            </div>
        </form>
    </div>
</div>
